import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import flagsData from '../../Public/flags-data.js';
import SkeletonImage from '../Skeleton/skeleton-image-component';
import * as authActions from '../../Redux/Auth/AuthActions.js'
import useClickOutside from '../../Hooks/useClickOutside.js';
import styled, { keyframes } from 'styled-components';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';


const rotate = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const Spinner = styled.div`
  border: 4px solid #f3f3f3;
  border-top: 4px solid #5262f2;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  animation: ${rotate} 1s linear infinite;
`;

const VotingCard = ({ selected, close }) => {

    const dispatch = useDispatch();

    const pollData = useSelector((state) => state.aiGen.pollData);
    const [isOpen, setIsOpen] = useState(selected);
    const [imageLoaded, setImageLoaded] = useState(false);
    const [selectedChoice, setSelectedChoice] = useState(null);
    const [animatedPercentages, setAnimatedPercentages] = useState([0, 0]);
    const [isLoading, setIsLoading] = useState(true);
    const votingModalRef = useRef(null);
    const navigate = useNavigate();

    useClickOutside(votingModalRef, (e) => close(e));

    useEffect(() => {
        setIsOpen(selected);

        // Enable or disable scrolling based on `isOpen`
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }

        return () => {
            document.body.style.overflow = '';
        };
    }, [selected, isOpen]);

    const handleVote = (choiceIndex) => {
        if (selectedChoice === null) {
            setSelectedChoice(choiceIndex);
            animatePercentages();
            setIsLoading(true);

            // Dispatch and navigate
            const redirectPath = '/poll';
            dispatch(authActions.redirectScreen(redirectPath));
            setTimeout(() => {
                navigate(redirectPath);
            }, 3000); // 3-second countdown before redirection
        }
    };

    const handleNext = () => {
        const redirectPath = '/poll';
        dispatch(authActions.redirectScreen(redirectPath));
        navigate(redirectPath);
    };

    const animatePercentages = () => {
        const totalVotes = pollData.choices.reduce((sum, choice) => sum + choice.votes, 0);
        const targetPercentages = pollData.choices.map((choice) =>
            Math.round((choice.votes / totalVotes) * 100)
        );
        const startPercentages = new Array(pollData.choices.length).fill(0);

        let start = null;

        const easeOutExpo = (t) => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t));

        const animate = (timestamp) => {
            if (!start) start = timestamp;
            const elapsed = timestamp - start;

            const progress = Math.min(elapsed / 1000, 1);
            const easedProgress = easeOutExpo(progress);
            const newPercentages = startPercentages.map((start, index) =>
                Math.round(start + (targetPercentages[index] - start) * easedProgress)
            );

            setAnimatedPercentages(newPercentages);

            if (progress < 1) {
                requestAnimationFrame(animate);
            }
        };

        requestAnimationFrame(animate);
    };

    const handleAllImagesLoad = () => {
        setTimeout(() => {
            setImageLoaded(true);
        }, 2000); // Add a slight delay for smooth transitions
    };

    
    return (
        <div>
            {pollData && pollData.choices?.length > 0 && (

                <div
                    className={`fixed inset-0 z-30 ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'} duration-500 transition-all ease-in-out`}
                >
                    {/* Semi-transparent dimmed background */}
                    <div className="absolute inset-0 bg-black bg-opacity-50"></div>

                    {/* Modal */}
                    <div
                        ref={votingModalRef}
                        className={`absolute bottom-0 w-full max-w-lg bg-white dark:bg-gray-900 rounded-t-3xl shadow-lg transition-transform ease-in-out duration-500 ${isOpen ? 'translate-y-0' : 'translate-y-full'
                            }`}
                    >
                        {/* Header Section */}
                        <div className="p-6 text-center">
                            <h1 className="text-4xl lg:text-5xl font-extrabold text-gray-800 dark:text-white mb-4">
                                Vote for the Next Video!
                            </h1>
                            <h2 className="text-2xl lg:text-4xl font-bold mt-0">
                                <span className="dark:text-blue-500 text-blue-700">
                                    {pollData.name} {flagsData.getFlagByCountry(pollData.name)}
                                </span>, which is your favorite?
                            </h2>

                            <h3 className="text-md font-normal mt-4 dark:text-gray-400 text-gray-500 ">
                                Vote for the upcoming video:
                                <span className="dark:text-blue-500 text-blue-700 font-bold">
                                    {' ' + pollData.title}
                                </span>
                            </h3>


                        </div>

                        {/* Images Section */}
                        <div className="flex grid-cols-2 grid-item justify-around px-3 gap-x-2">
                            {pollData.choices.slice(0, 2).map((choice, index) => (
                                <div
                                    key={index}
                                    className="relative w-48 lg:w-52 h-full flex items-center justify-center cursor-pointer"
                                    onClick={() => handleVote(index)}
                                >
                                    {/* Skeleton Loader */}
                                    {!imageLoaded && (
                                        <div className="absolute inset-0 flex items-center justify-center">
                                            <SkeletonImage />
                                        </div>
                                    )}

                                    {/* Image */}
                                    <img
                                        src={choice.imageUrl}
                                        alt={choice.name}
                                        className={`rounded-lg w-full h-full object-cover transition-opacity duration-700 ${imageLoaded ? 'opacity-100' : 'opacity-0'
                                            }`}
                                        onLoad={() => handleAllImagesLoad()}
                                    />

                                    {/* Overlay */}
                                    <div
                                        className={`absolute inset-0 transition-opacity duration-700 ease-in-out flex flex-col items-center justify-center ${selectedChoice === index ? 'opacity-100' : 'opacity-0 pointer-events-none'
                                            }`}
                                    >
                                        <div className="absolute inset-0 bg-black bg-opacity-70"></div>
                                        <div className="z-10 text-white text-5xl font-extrabold">
                                            {animatedPercentages[index]}%
                                        </div>
                                        <div className="text-blue-500 dark:text-blue-500 text-lg font-medium mb-2 z-10">
                                            {choice.votes} votes
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* <h3 className="text-md font-normal mt-4 dark:text-gray-400 text-gray-500 ">
                    Vote for the upcoming video:
                    <span className="dark:text-blue-500 text-blue-700 font-bold">
                        {' ' + pollData.title}
                    </span>
                </h3> */}

                        {/* Footer Section */}
                        <div className="p-4 flex justify-center gap-x-4">

                            {selectedChoice !== null ? (
                                <button
                                    className="px-4 py-2 bg-blue-500 text-white font-bold rounded-lg hover:bg-blue-600 transition flex items-center gap-x-2"
                                    onClick={handleNext}
                                >
                                    Next
                                    {isLoading && <Spinner />}
                                </button>
                            ) : (
                                <button
                                    className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition"
                                    onClick={close}
                                >
                                    Close
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default VotingCard;